// The operations snapshot and the exports (15 §15.1 "Operations snapshot", 07 §7.3 "Exports",
// 05 "Exports").
//
// FOR THE OFFICE SCREENS. Names in this file:
//
//   Document (server-written, one document, read with `dashboard.view` and scope `all`):
//     `snapshots/operations`  ->  `operationsSnapshotSchema` / `OperationsSnapshot`
//       .headline            `snapshotHeadlineSchema`        counts any director understands
//       .branches[]          `branchSnapshotSchema`          the map pins and the summary sheet
//       .exportHubs[]        `exportHubMeterSchema`          container meter: sea by boxes, air and Prime by kg
//       .destinationHubs[]   `destinationHubSchema`          Bulacan and any destination hub
//       .finance             `snapshotFinanceSchema`         per entity in its currency and in AED
//       .partners[] .ports[] `partnerSnapshotSchema` …       the network map's partner and port pins
//       .legs[]              `legSnapshotSchema`             containers, trips and handovers on the move
//     Written by the `operationsSnapshot` job every 10 minutes; a dev can run it on demand with
//     `runControlTowerNow {jobs: ['snapshot']}`.
//
//   Callables (online only):
//     `exportManifest`    input `exportManifestInputSchema`    -> `exportResultSchema`
//     `exportCollection`  input `exportCollectionInputSchema`  -> `exportResultSchema`  (super admin)
//     The result carries a Cloud Storage path under `exports/{uid}/`; the client downloads it with
//     the Storage SDK under the same sign-in (`getBlob(ref(storage, path))`). There is no signed URL.
//
//   Export log (super admin reads): `exportsLog/{exp_…}` -> `exportLogDocSchema`
//   Config kinds (saveConfig): `containerCapacity`, `airConsolidation` (in ./config.ts)
//   Lists for the admin export screen: `EXPORTABLE_COLLECTIONS`, `NEVER_EXPORTED_COLLECTIONS`
//   (the rule that strips secret-looking fields is `isSecretField` in @ecobox/domain)
import { z } from 'zod';
import { branchCodeSchema, sectorCodeSchema } from './config';

const int = z.number().int().nonnegative();
const kg = z.number().nonnegative();
const minor = z.number().int();
const iso = z.string();
const days = z.number().nonnegative();

export const SNAPSHOT_VERSION = 1;
/** The snapshot is one document: a hard ceiling keeps it far from Firestore's 1 MiB limit. */
export const SNAPSHOT_MAX_BYTES = 500 * 1024;

// ---- Building blocks -------------------------------------------------------------------------

/** Time in the current state, over boxes physically at one place. Days with two decimals. */
export const dwellSchema = z
  .object({
    count: int,
    avgDays: days,
    p90Days: days,
    oldestDays: days,
    /** Boxes there for more than 3, 7 and 14 days. */
    over3: int,
    over7: int,
    over14: int,
  })
  .strict();
export type Dwell = z.infer<typeof dwellSchema>;

/** Air and Prime are measured by weight: pieces, actual kg and chargeable kg (the larger of actual and volumetric). */
export const weightTotalsSchema = z
  .object({ pieces: int, actualKg: kg, chargeableKg: kg })
  .strict();
export type WeightTotals = z.infer<typeof weightTotalsSchema>;

/** Where boxes are in the journey at one branch. */
export const branchStagesSchema = z
  .object({
    /** At the branch, the shipment is not paid in full. */
    waitingPayment: int,
    /** At the branch, paid, but an active hold stops it. */
    held: int,
    /** At an origin branch, paid and not held: could be loaded today. */
    readyToShip: int,
    onTrip: int,
    /** Loaded in a container that has not left the export hub. */
    inContainer: int,
    /** At a destination hub waiting for our own delivery or a partner handover. */
    atHubWaiting: int,
    withPartner: int,
    outForDelivery: int,
  })
  .strict();
export type BranchStages = z.infer<typeof branchStagesSchema>;

const geoSchema = z
  .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
  .strict();

/** One branch of any country. */
export const branchSnapshotSchema = z
  .object({
    branchId: branchCodeSchema,
    name: z.string(),
    sectorId: sectorCodeSchema,
    countryName: z.string(),
    /** `origin` countries send cargo, `destination` countries receive it. */
    side: z.enum(['origin', 'destination']),
    type: z.enum(['hub', 'satellite']),
    parentHubId: branchCodeSchema.optional(),
    geo: geoSchema.optional(),
    stages: branchStagesSchema,
    /** Units physically at the branch (at the branch or loaded in a container there). */
    pieces: int,
    seaBoxes: int,
    air: weightTotalsSchema,
    prime: weightTotalsSchema,
    dwell: dwellSchema,
  })
  .strict();
export type BranchSnapshot = z.infer<typeof branchSnapshotSchema>;

// ---- Container meter -------------------------------------------------------------------------

/** One container size against the sea boxes ready at a hub. `percent` is of the minimum, capped at 100. */
export const containerFillSchema = z
  .object({
    containerType: z.enum(['20ft', '40ft', '40hc']),
    minBoxes: int,
    maxBoxes: int,
    /** floor(ready / minBoxes), capped at 100: the icon is full when there is enough for this size. */
    percent: z.number().int().min(0).max(100),
    /** Ready boxes reach the minimum: "enough for a 20ft". */
    enough: z.boolean(),
    /** Boxes still missing to reach the minimum (0 once enough). */
    shortBoxes: int,
    /** The same percent if every unpaid box were paid. */
    percentWithUnpaid: z.number().int().min(0).max(100),
    /** Whole containers of this size the ready boxes would fill (ready / minBoxes). */
    wholeContainers: int,
  })
  .strict();
export type ContainerFill = z.infer<typeof containerFillSchema>;

/** Air or Prime at an export hub, by weight. `targetKg` is null while no consolidation target is set. */
export const weightMeterSchema = z
  .object({
    readyPieces: int,
    actualKg: kg,
    chargeableKg: kg,
    unpaidPieces: int,
    unpaidChargeableKg: kg,
    targetKg: kg.nullable(),
    /** floor(chargeableKg / targetKg), capped at 100; null without a target. */
    percentOfTarget: z.number().int().min(0).max(100).nullable(),
    enough: z.boolean().nullable(),
  })
  .strict();
export type WeightMeter = z.infer<typeof weightMeterSchema>;

/** An origin export hub: its own boxes plus those at satellites that feed it. */
export const exportHubMeterSchema = z
  .object({
    hubId: branchCodeSchema,
    name: z.string(),
    sectorId: sectorCodeSchema,
    satelliteIds: z.array(branchCodeSchema),
    sea: z
      .object({
        /** Paid, not held, sea: at the hub or at its satellites. */
        readyBoxes: int,
        atHub: int,
        atSatellites: int,
        /** Sea boxes at the hub or satellites that would add once paid. */
        unpaidBoxes: int,
        /** Paid sea boxes on a trip to this hub: they will be ready on arrival. */
        onTripToHub: int,
        /** Sea boxes already loaded in an open container at the hub. */
        loadedInOpenContainer: int,
        containers: z.array(containerFillSchema),
      })
      .strict(),
    air: weightMeterSchema,
    prime: weightMeterSchema,
  })
  .strict();
export type ExportHubMeter = z.infer<typeof exportHubMeterSchema>;

// ---- Destination meter -----------------------------------------------------------------------

/** Cargo waiting at a destination hub for one of the two ways out. */
export const waitingGroupSchema = z
  .object({
    boxes: int,
    seaBoxes: int,
    air: weightTotalsSchema,
    prime: weightTotalsSchema,
    dwell: dwellSchema,
  })
  .strict();
export type WaitingGroup = z.infer<typeof waitingGroupSchema>;

export const destinationHubSchema = z
  .object({
    hubId: branchCodeSchema,
    name: z.string(),
    sectorId: sectorCodeSchema,
    ownDelivery: waitingGroupSchema,
    partnerHandover: waitingGroupSchema,
    withPartnerBoxes: int,
    outForDeliveryBoxes: int,
    /**
     * The same 20ft / 40ft / 40HC ranges as the export meter, against the sea boxes waiting at the
     * hub (own delivery plus partner handover): how many containers' worth is sitting there.
     */
    containers: z.array(containerFillSchema),
  })
  .strict();
export type DestinationHub = z.infer<typeof destinationHubSchema>;

// ---- Finance ---------------------------------------------------------------------------------

export const UPCOMING_KINDS = ['container', 'bill', 'advance', 'running'] as const;

export const upcomingItemSchema = z
  .object({
    id: z.string(),
    kind: z.enum(UPCOMING_KINDS),
    label: z.string(),
    dueDate: z.iso.date(),
    overdue: z.boolean(),
    /** In the entity's currency. */
    amountMinor: minor,
    amountAedMinor: minor.nullable(),
  })
  .strict();
export type UpcomingItem = z.infer<typeof upcomingItemSchema>;

/** The money figures of an entity, or the group in AED. All integer minor units. */
export const moneyFiguresSchema = z
  .object({
    cashMinor: minor,
    reservesMinor: minor,
    upcoming7Minor: minor,
    upcoming30Minor: minor,
    receivablesMinor: minor,
    collectedTodayMinor: minor,
    collectedMonthMinor: minor,
    shortBy7Minor: minor,
    shortBy30Minor: minor,
  })
  .strict();
export type MoneyFigures = z.infer<typeof moneyFiguresSchema>;

export const financeEntitySchema = z
  .object({
    entityId: sectorCodeSchema,
    name: z.string(),
    currency: z.string(),
    /** Cash in safes and banks, in the entity's currency (other wallets are not free cash). */
    cash: z.object({ safeMinor: minor, bankMinor: minor, totalMinor: minor }).strict(),
    reservesHeldMinor: minor,
    upcoming: z
      .object({
        calendar7Minor: minor,
        calendar30Minor: minor,
        bills7Minor: minor,
        bills30Minor: minor,
        advances7Minor: minor,
        advances30Minor: minor,
        /** The month's running costs from the budget (the cash calendar's `running` lines). */
        running7Minor: minor.default(0),
        running30Minor: minor.default(0),
        total7Minor: minor,
        total30Minor: minor,
        /** Already past due, and counted in both totals. */
        overdueMinor: minor,
        /** The next payments by due date (at most 15, within 30 days or overdue). */
        items: z.array(upcomingItemSchema),
        /** Items beyond the 15 shown. */
        moreItems: int,
      })
      .strict(),
    receivablesMinor: minor,
    receivableShipments: int,
    collectedTodayMinor: minor,
    collectedMonthMinor: minor,
    /** What the next 7 and 30 days need beyond cash plus reserves (0 when covered). */
    shortBy7Minor: minor,
    shortBy30Minor: minor,
    /** `shortBy30Minor > 0`. */
    short: z.boolean(),
    /** 1 unit of `currency` in AED (decimal text), or null when no rate converts it. */
    aedRate: z.string().nullable(),
    aed: moneyFiguresSchema.nullable(),
    /** Amounts left out because no FX rate converts them into the entity's currency. */
    unconverted: z.array(z.string()),
  })
  .strict();
export type FinanceEntity = z.infer<typeof financeEntitySchema>;

export const snapshotFinanceSchema = z
  .object({
    entities: z.array(financeEntitySchema),
    /** Sum of the entities that could be converted into AED. */
    groupAed: moneyFiguresSchema.nullable(),
    /** Entities left out of `groupAed` for want of an FX rate. */
    missingFx: z.array(sectorCodeSchema),
    /** `advances` and the cash calendar can overlap with recorded bills; the totals are the cautious reading. */
    note: z.string(),
  })
  .strict();
export type SnapshotFinance = z.infer<typeof snapshotFinanceSchema>;

// ---- Headline --------------------------------------------------------------------------------

export const snapshotHeadlineSchema = z
  .object({
    /** Units on the ground or on the way (every unit that is not delivered, returned, cancelled or lost). */
    unitsInSystem: int,
    waitingPayment: int,
    readyToShip: int,
    onTrip: int,
    inContainerAtOrigin: int,
    atSeaOrPort: int,
    atDestinationHubs: int,
    withPartners: int,
    outForDelivery: int,
    deliveredLast24h: int,
    shipmentsCollectedLast24h: int,
    containersInFlight: int,
    /** Containers that have sailed (`departing`, `departed`) and are not yet at the destination port. */
    containersAtSea: int,
    /** Containers arrived at the destination port and not yet pulled out (`arrived_port`). */
    containersAtPort: int,
    /**
     * The earliest last free day of port storage among the containers at port, or null. `daysLeft`
     * counts calendar days from today in the destination country (negative once passed).
     */
    nearestLastFreeDay: z
      .object({
        containerId: z.string(),
        containerNo: z.string(),
        date: z.iso.date(),
        daysLeft: z.number().int(),
      })
      .strict()
      .nullable(),
    pickupsWaiting: int,
    openAlerts: int,
    breachedAlerts: int,
    openExceptions: int,
    approvalsWaiting: int,
    /** Average days in the current state over every unit physically at a branch. */
    avgDwellDays: days,
    /** Sea boxes ready to load at the export hub that has the most. */
    bestHubReadyBoxes: int,
  })
  .strict();
export type SnapshotHeadline = z.infer<typeof snapshotHeadlineSchema>;

// ---- The network map: partners, ports and what is moving between places ------------------------

/** A partner on the map: where it is (when Settings has a pin) and what it holds now. */
export const partnerSnapshotSchema = z
  .object({
    partnerId: z.string(),
    name: z.string(),
    type: z.string(),
    geo: geoSchema.nullable(),
    /** Units handed over and not yet delivered, returned or written off, over open handovers. */
    openUnits: int,
    openHandovers: int,
  })
  .strict();
export type PartnerSnapshot = z.infer<typeof partnerSnapshotSchema>;

/** A port on the map (UN/LOCODE), with its pin when Settings has one. */
export const portSnapshotSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    kind: z.enum(['sea', 'air']),
    sectorId: sectorCodeSchema,
    geo: geoSchema.nullable(),
  })
  .strict();
export type PortSnapshot = z.infer<typeof portSnapshotSchema>;

/** One end of a leg: a branch, a partner or a port, with its pin when known. */
export const legEndSchema = z
  .object({
    kind: z.enum(['branch', 'partner', 'port']),
    id: z.string(),
    name: z.string(),
    geo: geoSchema.nullable(),
  })
  .strict();
export type LegEnd = z.infer<typeof legEndSchema>;

export const LEG_KINDS = ['container', 'trip', 'handover'] as const;
export type LegKind = (typeof LEG_KINDS)[number];

/**
 * Cargo on the move: a container that has left (`closed` to `pulled_out`), a trip between branches
 * on the road, or a partner handover with units still open. A leg whose ends have no pin is still
 * listed; the map only draws the ones it can place.
 */
export const legSnapshotSchema = z
  .object({
    id: z.string(),
    kind: z.enum(LEG_KINDS),
    /** `sea`, `air` or `prime` for a container; empty for trips and handovers. */
    service: z.string(),
    /** What people call it: the container number, the trip id or the partner. */
    label: z.string(),
    status: z.string(),
    from: legEndSchema,
    to: legEndSchema,
    /** The destination country's name, for the leg sheet. */
    destinationCountry: z.string(),
    units: int,
    /** Shipments on it, when the job can tell (a handover counts units only). */
    shipments: int.nullable(),
    /** When it left (ISO), when it is due (`YYYY-MM-DD`), when known. */
    departedAt: iso.nullable(),
    etd: z.iso.date().nullable(),
    eta: z.iso.date().nullable(),
    /** Days until `eta` from today (negative once passed), or null. */
    etaDaysLeft: z.number().int().nullable(),
    /** How far along, 0 to 1, from departure to the ETA; null when either is unknown. */
    progress: z.number().min(0).max(1).nullable(),
  })
  .strict();
export type LegSnapshot = z.infer<typeof legSnapshotSchema>;

// ---- The Philippines planning map (15 §15.5) --------------------------------------------------

const provinceCountsShape = {
  atOrigin: int,
  atSea: int,
  atHub: int,
  withPartner: int,
  outForDelivery: int,
};

/** The cargo for one destination province, wherever it is now (domain `province-plan.ts`). */
export const provinceSnapshotSchema = z
  .object({
    sectorId: sectorCodeSchema,
    province: z.string(),
    islandGroup: z.string(),
    ...provinceCountsShape,
    seaBoxes: int,
    ownBoxes: int,
    partnerBoxes: int,
    partners: z.array(z.object({ partnerId: z.string(), units: int }).strict()),
    oldestAtHubDays: z.number().nonnegative(),
    routes: z.array(z.object({ route: z.string(), ...provinceCountsShape }).strict()),
  })
  .strict();
export type ProvinceSnapshot = z.infer<typeof provinceSnapshotSchema>;

// ---- The document ----------------------------------------------------------------------------

export const operationsSnapshotSchema = z
  .object({
    version: z.literal(SNAPSHOT_VERSION),
    generatedAt: iso,
    /** The time budget the job took, for monitoring. */
    durationMs: int,
    source: z.enum(['schedule', 'dev']),
    /** What the job read, so a reader knows when the numbers are partial. */
    limits: z
      .object({
        boxesRead: int,
        boxCap: int,
        /** True when more boxes exist than the cap: the numbers are an undercount. */
        truncated: z.boolean(),
        shipmentsRead: int,
      })
      .strict(),
    headline: snapshotHeadlineSchema,
    branches: z.array(branchSnapshotSchema),
    exportHubs: z.array(exportHubMeterSchema),
    destinationHubs: z.array(destinationHubSchema),
    finance: snapshotFinanceSchema,
    /** The network map (absent on a snapshot written before it). */
    partners: z.array(partnerSnapshotSchema).default([]),
    ports: z.array(portSnapshotSchema).default([]),
    legs: z.array(legSnapshotSchema).default([]),
    /** The Philippines planning map (absent on a snapshot written before it). */
    provinces: z.array(provinceSnapshotSchema).default([]),
    /** Active units whose destination city is unknown, so they are on no province. */
    unplacedUnits: int.default(0),
  })
  .strict();
export type OperationsSnapshot = z.infer<typeof operationsSnapshotSchema>;

// ---- Exports ---------------------------------------------------------------------------------

export const MANIFEST_KINDS = ['container', 'trip', 'handover', 'run'] as const;
export type ManifestKind = (typeof MANIFEST_KINDS)[number];
export const MANIFEST_FORMATS = ['csv', 'xlsx'] as const;
export const COLLECTION_FORMATS = ['csv', 'jsonl', 'xlsx'] as const;
export type ExportFormat = (typeof COLLECTION_FORMATS)[number];

/** Permission each manifest needs, in the scope of the thing exported (07 §7.2). */
export const MANIFEST_PERMISSIONS = {
  container: 'container.manage',
  trip: 'trip.load',
  run: 'finalmile.plan',
  handover: 'partner.handover',
} as const;

const documentIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{2,59}$/, 'Not a valid id');

export const exportManifestInputSchema = z
  .object({
    kind: z.enum(MANIFEST_KINDS),
    id: documentIdSchema,
    format: z.enum(MANIFEST_FORMATS),
  })
  .strict();
export type ExportManifestInput = z.infer<typeof exportManifestInputSchema>;

export const EXPORT_MAX_ROWS = 50_000;
export const EXPORT_MAX_FILTERS = 3;
export const EXPORT_FILTER_OPS = [
  '==',
  '!=',
  '<',
  '<=',
  '>',
  '>=',
  'in',
  'array-contains',
] as const;
export const exportFieldSchema = z
  .string()
  .regex(/^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*){0,5}$/, 'Not a field path')
  .max(100);

const scalar = z.union([z.string().max(200), z.number(), z.boolean()]);

export const exportFilterSchema = z
  .object({
    field: exportFieldSchema,
    op: z.enum(EXPORT_FILTER_OPS),
    value: z.union([scalar, z.array(scalar).min(1).max(30)]),
  })
  .strict();
export type ExportFilter = z.infer<typeof exportFilterSchema>;

export const exportCollectionInputSchema = z
  .object({
    collection: z.string().regex(/^[A-Za-z][A-Za-z0-9]{1,40}$/, 'Not a collection name'),
    where: z.array(exportFilterSchema).max(EXPORT_MAX_FILTERS).optional(),
    orderBy: z
      .object({ field: exportFieldSchema, direction: z.enum(['asc', 'desc']).default('asc') })
      .strict()
      .optional(),
    limit: z.number().int().min(1).max(EXPORT_MAX_ROWS),
    format: z.enum(COLLECTION_FORMATS),
  })
  .strict();
export type ExportCollectionInput = z.infer<typeof exportCollectionInputSchema>;

/** What both export callables return. Download `path` with the Storage SDK under the same sign-in. */
export const exportResultSchema = z
  .object({
    exportId: z.string().regex(/^exp_[0-9A-HJKMNP-TV-Z]{26}$/),
    path: z.string().regex(/^exports\/[^/]+\/exp_[0-9A-HJKMNP-TV-Z]{26}\.(csv|xlsx|jsonl)$/),
    filename: z.string(),
    contentType: z.string(),
    format: z.enum(COLLECTION_FORMATS),
    rowCount: int,
    bytes: int,
    /** The file is deleted after this; download it before. */
    expiresAt: iso,
  })
  .strict();
export type ExportResult = z.infer<typeof exportResultSchema>;

/** `exportsLog/{exp_…}`: who exported what, with which filters, how many rows, when. */
export const exportLogDocSchema = z.object({
  id: z.string(),
  kind: z.enum(['manifest', 'collection']),
  /** The collection name, or `{manifest kind}:{id}`. */
  subject: z.string(),
  collection: z.string().optional(),
  filters: z
    .object({
      where: z.array(exportFilterSchema).default([]),
      orderBy: z.object({ field: z.string(), direction: z.string() }).optional(),
      limit: int.optional(),
    })
    .optional(),
  format: z.enum(COLLECTION_FORMATS),
  status: z.enum(['running', 'done', 'failed', 'deleted']),
  rowCount: int,
  bytes: int,
  byUid: z.string(),
  byName: z.string(),
  createdAt: iso,
  finishedAt: iso.optional(),
  expiresAt: iso,
  path: z.string(),
  filename: z.string(),
  error: z.string().optional(),
});
export type ExportLogDoc = z.infer<typeof exportLogDocSchema>;

/**
 * Collections a super admin may export. Business tables only. Anything that can hold a token, a
 * hash or a secret is on the never list below, and fields that look like secrets are removed from
 * every row of every collection (see `isSecretField`).
 */
export const EXPORTABLE_COLLECTIONS = [
  // Configuration
  'sectors',
  'branches',
  'ports',
  'localZones',
  'destinationCities',
  'boxSizes',
  'rateCards',
  'emptyBoxPrices',
  'branchSettings',
  'vehicles',
  'partners',
  'coverage',
  'transportVendors',
  'costModels',
  'seasons',
  'slaPolicies',
  'barcodeBatches',
  'barcodes',
  // Customers and cargo
  'customers',
  'shipments',
  'boxes',
  'pickupRequests',
  'exceptions',
  'emptyBoxStock',
  'stockMovements',
  // Movement and destination
  'trips',
  'containers',
  'handovers',
  'partnerUpdates',
  'runs',
  'emptyBoxTransfers',
  'partnerScorecards',
  // Money
  'payments',
  'tills',
  'dayCloses',
  'wallets',
  'walletBalances',
  'journals',
  'reserveAllocations',
  'reserveTotals',
  'approvals',
  'bills',
  'advances',
  'intercompanyInvoices',
  'cashCalendar',
  'fxRates',
  'driverCustody',
  'driverCashHandovers',
  // Control and messages
  'alerts',
  'notificationOutbox',
  'devices',
  'numberLeases',
  // People and audit
  'staff',
  'roles',
  'commands',
  'auditLog',
  'exportsLog',
] as const;
export type ExportableCollection = (typeof EXPORTABLE_COLLECTIONS)[number];

/** Never exportable, whoever asks: they hold secrets, tokens, hashes or one-time codes. */
export const NEVER_EXPORTED_COLLECTIONS = [
  'outboxSecrets',
  'testInboxSecrets',
  'handoverSecrets',
  'deliveryCodes',
  'partnerLinkRates',
  'commandAttempts',
  'otpChallenges',
  'publicApiRates',
  'exportRates',
  'partnerRefIndex',
  'billKeys',
  'billClearings',
  'counters',
  'receipts',
  'customerPhones',
  'ledgerApplied',
  'openTills',
] as const;
